import { useState, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { useGetUserFeedbacks } from 'src/modules/profile/services/profileService';

import { Label } from 'src/components/label';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomTabs } from 'src/components/custom-tabs';

import { FeedbackItem } from './feedback-item';
import { FeedbackDetail } from './feedback-detail';
import { FeedbackButton } from './feedback-button';

// ----------------------------------------------------------------------

export function FeedbacksContent() {
  const [currentTab, setCurrentTab] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  // Fetch feedbacks from API
  const { feedbacks, feedbacksLoading, feedbacksError } = useGetUserFeedbacks();

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const handleFeedbackClick = useCallback((feedback) => {
    setSelectedFeedback(feedback);
  }, []);

  const handleBackToFeedbacks = useCallback(() => {
    setSelectedFeedback(null);
  }, []);

  // Calculate feedback counts
  const repliedFeedbacksCount = feedbacks.filter((f) => f.Status === 'Replied').length;
  const reviewingFeedbacksCount = feedbacks.filter((f) => f.Status === 'Reviewing').length;

  // Update feedback tab counts
  const tabs = [
    { value: 'all', label: 'All', count: feedbacks.length },
    { value: 'replied', label: 'Replied', count: repliedFeedbacksCount },
    { value: 'reviewing', label: 'Reviewing', count: reviewingFeedbacksCount },
  ];

  // Filter feedbacks based on current tab
  const filteredFeedbacks = feedbacks.filter((feedback) => {
    if (currentTab === 'replied') return feedback.Status === 'Replied';
    if (currentTab === 'reviewing') return feedback.Status === 'Reviewing';
    return true; // 'all' tab
  });

  // If a feedback is selected, show detail view
  if (selectedFeedback) {
    return <FeedbackDetail feedback={selectedFeedback} onBack={handleBackToFeedbacks} />;
  }

  // Loading state
  if (feedbacksLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading feedbacks...
        </Typography>
      </Stack>
    );
  }

  // Error state
  if (feedbacksError) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <Typography variant="body1" color="error">
          Error loading feedbacks
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {feedbacksError.message || 'Please try again later'}
        </Typography>
      </Stack>
    );
  }

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Feedbacks
      </Typography>
      <FeedbackButton data-slot="feedback" />
    </Stack>
  );

  const renderTabs = (
    <CustomTabs variant="fullWidth" value={currentTab} onChange={handleChangeTab}>
      {tabs.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'replied' && 'success') ||
                (tab.value === 'reviewing' && 'info') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </CustomTabs>
  );

  const renderList = (
    <Scrollbar>
      <Box component="ul">
        {filteredFeedbacks.length > 0 ? (
          filteredFeedbacks.map((feedback) => (
            <Box component="li" key={feedback.Id} sx={{ display: 'flex' }}>
              <FeedbackItem feedback={feedback} onClick={handleFeedbackClick} />
            </Box>
          ))
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No feedbacks found
            </Typography>
          </Box>
        )}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      {renderHead}
      {renderTabs}
      {renderList}
    </>
  );
}

FeedbacksContent.propTypes = {};
